import React, { useEffect } from 'react';
import { Mail, Phone, MapPin, FileText, Shield, User, Briefcase, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { BrokerDto } from 'api/broker';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { ReferentialKey } from 'api/referential';
import { usePanel } from 'hooks/usePanel';

export const BrokerSummary: React.FC<{ broker: BrokerDto, onLinkBroker: () => void }> = ({ broker, onLinkBroker }) => {
  const referentialsData = useAppSelector(selectReferentialData);
  const { setPanelActions, closePanel } = usePanel();

  useEffect(() => {
    if (broker) {
      setPanelActions({
        closeLabel: "COMMON.CANCEL",
        submitLabel: "COMMON.VALIDATE",
        onSubmit: () => {
          onLinkBroker()
        },
      });
    }

    return () => {
      setPanelActions(undefined);
    }
  }, [setPanelActions, broker, onLinkBroker, closePanel]);


  const getReferentialLabel = (referentialKey: ReferentialKey, id?: string | null) => {
    const referential = referentialsData[referentialKey]?.find((item: any) => item.id === id);
    return referential ? (referential.label as string) : undefined;
  };

  const getReferentialLabelByCode = (referentialKey: ReferentialKey, code?: string | null) => {
    const referential = referentialsData[referentialKey]?.find((item: any) => item.code === code);
    return referential ? (referential.label as string) : undefined;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (date: Date | null | undefined) => {
    if (!date) return 'Jamais connecté';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
      {/* Header */}
      <div className="bg-primary p-8 border-b border-primary/10">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <Briefcase className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white">{broker.companyName}</h2>
              <p className="text-sm text-gray-200 mt-1">
                Intermédiaire
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-3 py-1 bg-white/10 text-white text-xs font-semibold rounded-full border border-white/20">
                  SIRET: {broker.registrationNumber}
                </span>
              </div>
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm text-gray-200">Capital social</p>
            <p className="text-2xl font-bold text-white">{formatCurrency(broker.capital)}</p>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Contact Information */}
        <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Coordonnées
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
              <Mail className="w-5 h-5 text-primary/70" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium">Email réclamations</p>
                <p className="text-sm text-gray-900 truncate">{broker.complaintEmail}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
              <Phone className="w-5 h-5 text-primary/70" />
              <div>
                <p className="text-xs text-gray-500 font-medium">Téléphone</p>
                <p className="text-sm text-gray-900">{broker.phone}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Address */}
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Adresse du siège
            </h3>
            <div className="bg-white rounded-lg p-4 border border-primary/10">
              {broker.address ? (
                <div className="space-y-1">
                  {broker.address.line1 && (
                    <p className="text-sm text-gray-900">{broker.address.line1}</p>
                  )}
                  {broker.address.line2 && (
                    <p className="text-sm text-gray-900">{broker.address.line2}</p>
                  )}
                  {broker.address.line3 && (
                    <p className="text-sm text-gray-900">{broker.address.line3}</p>
                  )}
                  {broker.address.zipCode && broker.address.city && (
                    <p className="text-sm text-gray-900">
                      {broker.address.zipCode} {broker.address.city}
                    </p>
                  )}
                  {broker.address.country && (
                    <p className="text-sm text-gray-600 mt-2">
                      {getReferentialLabelByCode(ReferentialKey.Country, broker.address.country)}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Adresse non renseignée</p>
              )}
            </div>
          </div>

          {/* Registration Information */}
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Immatriculation
            </h3>
            <div className="space-y-3">
              {broker.rcsCity && (
                <div className="flex justify-between items-center py-2 bg-white rounded-lg px-4 border border-primary/10">
                  <span className="text-sm text-gray-600">RCS</span>
                  <span className="text-sm font-medium text-gray-900">{broker.rcsCity}</span>
                </div>
              )}
              {broker.rcsNumber && (
                <div className="flex justify-between items-center py-2 bg-white rounded-lg px-4 border border-primary/10">
                  <span className="text-sm text-gray-600">N° RCS</span>
                  <span className="text-sm font-medium text-gray-900">{broker.rcsNumber}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Professional Information */}
        {(broker.oriasNumber || broker.rcp) && (
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Informations professionnelles
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {broker.oriasNumber && (
                <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Numéro ORIAS</p>
                    <p className="text-sm font-semibold text-gray-900">{broker.oriasNumber}</p>
                  </div>
                </div>
              )}
              {broker.rcp && (
                <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">RCP</p>
                    <p className="text-sm font-semibold text-gray-900">{broker.rcp}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Broker User Information */}
        {broker.brokerUser && (
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Utilisateur du courtier
            </h3>
            <div className="bg-white rounded-lg p-4 border border-primary/10">
              <div className="space-y-4">
                {/* User Identity */}
                <div className="flex items-center gap-3 pb-4 border-b border-primary/10">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-base font-semibold text-primary">
                      {broker.brokerUser.firstName.charAt(0)}{broker.brokerUser.lastName.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {broker.brokerUser.firstName} {broker.brokerUser.lastName}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Mail className="w-3.5 h-3.5 text-gray-500" />
                      <p className="text-sm text-gray-600">{broker.brokerUser.email}</p>
                    </div>
                  </div>
                  <div>
                    {broker.brokerUser.isActive ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/10 text-green-700 text-xs font-semibold rounded-full border border-green-500/20">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/10 text-red-700 text-xs font-semibold rounded-full border border-red-500/20">
                        <XCircle className="w-3.5 h-3.5" />
                        Inactif
                      </span>
                    )}
                  </div>
                </div>

                {/* Dates */}
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/10">
                    <Calendar className="w-4 h-4 text-primary/70" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Date de création</p>
                      <p className="text-sm text-gray-900">{formatDate(broker.brokerUser.creationDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/10">
                    <Calendar className="w-4 h-4 text-primary/70" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Dernière connexion</p>
                      <p className="text-sm text-gray-900">{formatDateTime(broker.brokerUser.lastConnectionDate)}</p>
                    </div>
                  </div>
                </div>

                {/* Plateformes and Roles */}
                {broker.brokerUser.plateformeIds && (broker.brokerUser.plateformeIds.length > 0 || broker.brokerUser.roleIds.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-3 pt-3 border-t border-primary/10">
                    {broker.brokerUser.plateformeIds.length > 0 && (
                      <div className="p-3 bg-primary/5 rounded-lg border border-primary/10">
                        <p className="text-xs text-gray-500 font-medium mb-2">Plateformes</p>
                        <div className="flex flex-wrap gap-1.5">
                          {broker.brokerUser.plateformeIds.map((orgId, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 bg-white text-primary text-xs font-medium rounded border border-primary/20"
                            >
                              {orgId}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {broker.brokerUser.roleIds.length > 0 && (
                      <div className="p-3 bg-primary/5 rounded-lg border border-primary/10">
                        <p className="text-xs text-gray-500 font-medium mb-2">Rôles</p>
                        <div className="flex flex-wrap gap-1.5">
                          {broker.brokerUser.roleIds.map((roleId, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 bg-white text-primary text-xs font-medium rounded border border-primary/20"
                            >
                              {roleId}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
